import DateFormat from '@/Enums/Format/DateFormat.enum';
import LookerReportType from '@/Enums/Looker/ReportType.enum';
import type { ISegmentControlOption } from '@/Interfaces/ControlOption.interface';
import type { IParsedEvent } from '@/Interfaces/Pixels/Event.interface';
import type { IPixelReportRecord } from '@/Interfaces/Pixels/Report.interface';
import type { IBaseReportResponse } from '@/Interfaces/Reporting/Report.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { MAX_PIXELS_QUERY_PARAMS_LENGTH } from '@/constants/axios';
import { EVENTS_ENDPOINT, eventsWithIdEndpoint } from '@/constants/endpoints';
import batchRequest from '@/helpers/batchRequest';
import { fetchLookerReport } from '@/helpers/beeswaxReports';
import { getAdvertiserFilter } from '@/helpers/filters/filterMapping';
import { processApiPixelsResponse, processDbSegmentsResponse, trimPixelTag } from '@/helpers/pixels';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { HttpStatusCode } from 'axios';
import exportFromJSON from 'export-from-json';
import moment from 'moment';
import type { RootState } from '..';
import { fetchSegments } from '../api/pixels.api';
import { selectRibeyeWithBeeswaxClients, selectRibeyeWithBeeswaxWhitelabels } from '../selectors/sharedApi.selectors';

export const archiveEventsWithIdEndpoint = (id: IParsedEvent['id']) => `${EVENTS_ENDPOINT}/setActive/${id}`;
export const reportEndpoint = '/pixels/report';

enum Action {
  GetPixels = 'pixelsSlice/getPixels',
  GetPixelsReport = 'pixelsSlice/getPixelsReport',
  DownloadPixelReport = 'pixelsSlice/downloadPixelReport',
  GetSegments = 'pixelsSlice/getSegments',
  GetPixelTag = 'pixelsSlice/getPixelTag',
  UpdateActiveStatus = 'pixelsSlice/updateActiveStatus',
}

export const getPixels = createAsyncThunk<IParsedEvent[], IAbortSignalThunkArg>(
  Action.GetPixels,
  async (payload, { getState }) => {
    const state = getState() as RootState;
    const {
      pixels: { filters },
      me: { role },
    } = state;

    const whitelabels = selectRibeyeWithBeeswaxWhitelabels(state);
    const clients = selectRibeyeWithBeeswaxClients(state);

    const advertiserIds = getAdvertiserFilter(filters, role!, clients, whitelabels, 'value');

    if (!advertiserIds?.length) return [];

    const batchFetchPixels = batchRequest(async advertiserIdBatch => {
      const response = await axios.get(EVENTS_ENDPOINT, {
        params: {
          active: filters.active,
          advertiserIds: advertiserIdBatch,
        },
        signal: payload?.signal,
      });
      return response.data || [];
    }, MAX_PIXELS_QUERY_PARAMS_LENGTH);

    const pixels = await batchFetchPixels(advertiserIds);

    return processApiPixelsResponse(pixels);
  },
);

export const getPixelsReport = createAsyncThunk<IBaseReportResponse<IPixelReportRecord>, IAbortSignalThunkArg>(
  Action.GetPixelsReport,
  async (payload, { getState }) => {
    const state = getState() as RootState;
    const {
      pixels: { filters },
      me: { role },
    } = state;

    const whitelabels = selectRibeyeWithBeeswaxWhitelabels(state);
    const clients = selectRibeyeWithBeeswaxClients(state);

    const advertiserIds = getAdvertiserFilter(filters, role!, clients, whitelabels, 'value');

    if (!advertiserIds?.length) return { report: [] };

    return await fetchLookerReport({
      signal: payload?.signal,
      url: reportEndpoint,
      body: {
        reportType: LookerReportType.EventFiresPreview,
        filters: {
          advertiserIds,
          dateRangeStart: moment().format(DateFormat.Iso8601),
          dateRangeEnd: moment().format(DateFormat.Iso8601),
        },
      },
    });
  },
);

export const downloadPixelReport = createAsyncThunk<
  void,
  IAbortSignalThunkArg & { dateRangeStart: string; id: number; name: string }
>(Action.DownloadPixelReport, async payload => {
  const response = await fetchLookerReport({
    url: reportEndpoint,
    signal: payload?.signal,
    body: {
      reportType: LookerReportType.EventFiresByDay,

      filters: {
        dateRangeStart: moment(payload.dateRangeStart).format(DateFormat.Iso8601),
        dateRangeEnd: moment().format(DateFormat.Iso8601),
        eventIds: [payload.id],
      },
    },
  });

  exportFromJSON({
    data: (response.report ?? []).map(({ id, ...row }: IPixelReportRecord) => row),
    fileName: `${payload.id}_${payload.name?.replace(/\s/g, '')}`,
    exportType: exportFromJSON.types.csv,
  });
});

export const getSegments = createAsyncThunk<ISegmentControlOption[], IAbortSignalThunkArg>(
  Action.GetSegments,
  async payload => {
    const segments = await fetchSegments(payload);

    return processDbSegmentsResponse(segments || []);
  },
);

export const getPixelTag = createAsyncThunk<string, IAbortSignalThunkArg | undefined>(
  Action.GetPixelTag,
  async (payload, { getState, rejectWithValue }) => {
    let {
      pixels: { selectedPixel },
    } = getState() as RootState;
    const response = await axios.get(eventsWithIdEndpoint(selectedPixel.id!), {
      signal: payload?.signal,
    });

    if (response.status !== HttpStatusCode.Ok) {
      return rejectWithValue(response.statusText || 'An error occurred while fetching the pixel tag.');
    }

    return trimPixelTag(response.data?.[0]?.tag);
  },
);

export const updateActiveStatus = createAsyncThunk<{ id: number }, { id: number; active: boolean }>(
  Action.UpdateActiveStatus,
  async (payload, { rejectWithValue }) => {
    const response = await axios.put(archiveEventsWithIdEndpoint(payload.id), {
      active: payload.active,
    });

    if (response.status !== HttpStatusCode.Ok) {
      return rejectWithValue(response.statusText || 'Failed to archive pixel.');
    }

    return { id: payload.id };
  },
);
